import React, { useState, useEffect, useCallback } from 'react';
import AuthContext from './AuthContext';
import { getCurrentUser, loginUser, logoutUser } from '../DatabaseAPI';
import { API_BASE } from '../api/transport';
import {
  markFullSignOutInProgress,
  markFullSignOutCompleted,
  markFullSignOutFailed,
  isFullSignOutBlocked,
  clearFullSignOutState,
  clearClerkBridgeStorage
} from './fullSignOutState';
import { describeUser, describeError } from './authLogging';
import { clearAdmissionToken } from './betaAdmissionToken';

const STORAGE_KEY = 'authenticatedUser';
// Fix 5: use absolute API origin so the balance fetch resolves correctly in
// direct-mode dev (where the frontend dev server proxies to a different port).
const CURRENCY_BALANCE_URL = `${API_BASE}/v2/currency/balance/`;
let logoutInFlight = null;

function AuthProvider({ children, clerkSignOut = null }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [fullSignOutVersion, setFullSignOutVersion] = useState(0);

  // Mythril balance — seeded from live GET /api/v2/currency/balance/ when authenticated.
  // Defaults to 0 until the endpoint responds. Updated after purchases via balance_after.
  const [mythrilBalance, setMythrilBalance] = useState(0);

  // Hydrate Mythril balance from live endpoint when authenticated.
  // Resets to 0 immediately when unauthenticated (logout or session expiry).
  // Cancellation guard prevents a stale in-flight response from overwriting the reset.
  useEffect(() => {
    if (!isAuthenticated) {
      setMythrilBalance(0);
      return;
    }
    let cancelled = false;
    fetch(CURRENCY_BALANCE_URL, {
      method: 'GET',
      credentials: 'include',
    })
      .then(res => {
        if (!res.ok) throw new Error(`currency/balance/ returned ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (!cancelled) {
          const balance = typeof data.current_balance === 'number' ? data.current_balance : 0;
          setMythrilBalance(balance);
        }
      })
      .catch(err => {
        if (!cancelled) {
          // Do NOT reset to 0 on fetch error — leave the cached value intact so
          // a transient network failure (e.g. a failed league join) does not wipe
          // the displayed balance. The user's real balance is unchanged server-side.
          console.error('AuthProvider: Failed to fetch Mythril balance — retaining cached value:', err);
        }
      });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // ClerkStatusBootstrap owns the single status request, which also establishes CSRF.
  useEffect(() => {
    const initializeAuth = async () => {
      await checkCachedAuth();
    };

    initializeAuth();
  }, []);

  const checkCachedAuth = async () => {
    setIsLoading(true);

    // A prior partial logout is a recovery state, not an invitation to probe the
    // surviving Django cookie. Keep the user blocked until the explicit retry
    // completes; otherwise a refresh could silently restore authentication.
    if (isFullSignOutBlocked()) {
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
      return;
    }
    
    try {
      const cachedUser = localStorage.getItem(STORAGE_KEY);
      if (cachedUser) {
        console.log('AuthProvider: Found cached user, validating with backend...');
        try {
          JSON.parse(cachedUser);
        } catch (parseError) {
          console.error('AuthProvider: Error parsing cached user:', parseError);
          localStorage.removeItem(STORAGE_KEY);
        }
      } else {
        console.log('AuthProvider: No cached user found; validating Django session directly...');
      }

      // localStorage is only a cache. The Django session cookie is the source of truth,
      // so this call is intentionally made whether or not a cached entry exists.
      const validatedUserResponse = await getCurrentUser();
      const validatedUser = validatedUserResponse?.user || validatedUserResponse;

      if (validatedUser && validatedUser.id) {
          // Log shape, never content: the user object contains email/username/public_id.
          console.log('AUTH: Backend returned fresh user data', describeUser(validatedUser));
          
          // CRITICAL: Always use fresh backend data - never use cached for user ID
          const userWithDebugPref = {
            ...validatedUser,
            starts_in_debug: validatedUser.mythic_profile?.starts_in_debug || false
          };
          
          setUser(userWithDebugPref);
          setIsAuthenticated(true);
          
          // Update localStorage with fresh user data (replace any old cached data)
          localStorage.setItem(STORAGE_KEY, JSON.stringify(userWithDebugPref));
          console.log('AUTH: Updated cached user from backend');
      } else {
        console.log('❌ AUTH: Backend validation failed - CLEARING CACHE to force re-authentication');
        localStorage.removeItem(STORAGE_KEY);
        setUser(null);
        setIsAuthenticated(false);
        console.log('🔍 AUTH: Cache cleared - user must re-authenticate with fresh backend data');
      }
    } catch (error) {
      console.error('AuthProvider: Error validating cached auth', describeError(error));
      
      // Check if this is an authentication error
      if (error.status === 401 || error.status === 403 || error.message.includes('Authentication failed')) {
        console.log('AuthProvider: Authentication error detected, clearing cache');
        localStorage.removeItem(STORAGE_KEY);
        setUser(null);
        setIsAuthenticated(false);
      } else {
        // CRITICAL FIX: Don't use cached fallback data - it may contain wrong UUID
        console.log('❌ AUTH: Network/other error - CLEARING CACHE instead of using fallback');
        console.log('🔍 AUTH: This prevents using cached data with wrong UUID user IDs');
        
        // Clear any potentially corrupted cached data
        localStorage.removeItem(STORAGE_KEY);
        setUser(null);
        setIsAuthenticated(false);
        
        console.log('✅ AUTH: Forced clean state - user must re-authenticate with fresh backend data');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      // Never log the submitted email or any other credential field.
      console.log('AuthProvider: Attempting login');
      
      const response = await loginUser(credentials);
      
      if (response.status === 'success' && response.user) {
        console.log('AuthProvider: Login successful', describeUser(response.user));
        
        // Add starts_in_debug from user profile if available
        const userWithDebugPref = {
          ...response.user,
          starts_in_debug: response.user.mythic_profile?.starts_in_debug || false
        };
        
        setUser(userWithDebugPref);
        setIsAuthenticated(true);
        
        // Cache user in localStorage
        localStorage.setItem(STORAGE_KEY, JSON.stringify(userWithDebugPref));
        
        return { success: true, user: userWithDebugPref };
      } else {
        // The backend message can echo submitted values; keep it out of the log
        // while still returning it to the caller for display.
        console.error('AuthProvider: Login failed');
        return { success: false, message: response.message || 'Login failed' };
      }
    } catch (error) {
      console.error('AuthProvider: Login error', describeError(error));
      return { success: false, message: 'Login failed. Please check your credentials.' };
    }
  };

  const logout = () => {
    if (logoutInFlight) return logoutInFlight;

    logoutInFlight = (async () => {
      console.log('AuthProvider: Starting full sign-out propagation');
      markFullSignOutInProgress();
      const failures = [];

      // End the application session before invoking Clerk. Clerk may perform a
      // browser-level navigation as part of a successful signOut(), which abandons
      // this JavaScript continuation. Django logout must therefore never depend on
      // code after the Clerk await being reached.
      try {
        await logoutUser();
      } catch (error) {
        console.error('AuthProvider: Django sign-out failed', describeError(error));
        failures.push({ system: 'Django', error });
      }

      // Clear state only after both attempts have been made. Keep the completed
      // guard until Clerk reports signed out so a stale coordinator render cannot bridge.
      setUser(null);
      setIsAuthenticated(false);
      setMythrilBalance(0); // belt-and-suspenders: also reset via the isAuthenticated effect above
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem('selectedTeamId');
      sessionStorage.removeItem('selectedLeagueId');
      sessionStorage.removeItem('previousLocation');
      sessionStorage.removeItem('selectedPlayerId');
      sessionStorage.removeItem('selectedLeagueMode');
      sessionStorage.removeItem('currentScreen');
      sessionStorage.removeItem('opponentTeamId');
      sessionStorage.removeItem('opponentTeamName');
      sessionStorage.removeItem('viewMode');
      sessionStorage.removeItem('playerViewMode');
      sessionStorage.removeItem('expandPlayerOnGearPage');
      sessionStorage.removeItem('showGearTutorial');
      sessionStorage.removeItem('selectedBossLeagueId');
      localStorage.removeItem('durableTeamContext');
      localStorage.removeItem('draftTeamId');
      clearClerkBridgeStorage();
      // A pending beta admission token belongs to the signup attempt that is now
      // over. It is single-use and short-lived, but leaving it behind would let it
      // attach to whoever signs up next in this tab — where it can only produce a
      // confusing rejection for a credential they never supplied.
      clearAdmissionToken();

      // If Django logout succeeded, persist a completion guard before calling
      // Clerk. A real Clerk navigation can interrupt this function immediately;
      // after remount, ClerkBridgeCoordinator clears COMPLETED only after Clerk
      // reports signed out. This prevents both a stale IN_PROGRESS recovery screen
      // and an accidental re-bridge during the transition.
      if (failures.length === 0) {
        markFullSignOutCompleted();
      } else {
        markFullSignOutFailed();
      }

      // A missing hook means the app is on the legacy path, where there is no
      // Clerk session to terminate. Always attempt Clerk even if Django failed so
      // the two independent sessions cannot silently diverge.
      if (typeof clerkSignOut === 'function') {
        try {
          await clerkSignOut();
        } catch (error) {
          console.error('AuthProvider: Clerk sign-out failed', describeError(error));
          failures.push({ system: 'Clerk', error });
          markFullSignOutFailed();
        }
      }

      if (failures.length > 0) {
        markFullSignOutFailed();
        setFullSignOutVersion((version) => version + 1);
        const detail = failures.map(({ system, error }) => `${system}: ${error?.message || String(error)}`).join('; ');
        throw new Error(`Full sign-out propagation failed. ${detail}`);
      }
      if (typeof clerkSignOut !== 'function') {
        clearFullSignOutState();
      }
      return { success: true };
    })().finally(() => {
      logoutInFlight = null;
    });

    return logoutInFlight;
  };

  const checkAuthStatus = async () => {
    // Re-validate current authentication
    return checkCachedAuth();
  };

  // Method to manually set user after successful registration (when backend auto-logs in)
  const setAuthenticatedUser = (userData) => {
    console.log('AuthProvider: Setting authenticated user', describeUser(userData));
    
    const userWithDebugPref = {
      ...userData,
      starts_in_debug: userData.mythic_profile?.starts_in_debug || false
    };
    
    setUser(userWithDebugPref);
    setIsAuthenticated(true);
    
    // Cache user in localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(userWithDebugPref));
    
    return { success: true, user: userWithDebugPref };
  };

  const contextValue = {
    user,
    isAuthenticated,
    isLoading,
    fullSignOutVersion,
    login,
    logout,
    checkAuthStatus,
    setAuthenticatedUser,
    mythrilBalance,
    setMythrilBalance,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;
